import React, { useState } from 'react';
import { useSchool as useOps } from '../context/SchoolContext';
import { DollarSign, Activity, Save, RefreshCw, Shield, Bell, Lock } from 'lucide-react';
import './Settings.css';

const Settings = () => {
    const { loading } = useOps();

    const [currencies, setCurrencies] = useState([
        { code: 'USD', rate: 1.0, symbol: '$' },
        { code: 'ZWG', rate: 13.5, symbol: 'ZWG' },
        { code: 'ZAR', rate: 18.2, symbol: 'R' }
    ]);

    const [thresholds, setThresholds] = useState({
        minLessonsForTest: 20,
        maxLessonsPerDay: 2,
        cancellationWindowHours: 24
    });

    const handleSave = () => {
        alert('Settings Saved! (Simulation)');
    };

    return (
        <div className="page-container settings-container">
            <div className="page-header border-b border-slate-200/60 pb-8">
                <div>
                    <h2 className="page-title">System Configuration</h2>
                    <p className="page-subtitle">Tune operational parameters and financial defaults</p>
                </div>
                <button
                    onClick={handleSave}
                    className="btn btn-primary flex items-center gap-2 px-8"
                >
                    <Save size={18} /> Apply Changes
                </button>
            </div>

            <div className="settings-grid fade-in">
                {/* Currency Matrix */}
                <div className="settings-card shadow-lg">
                    <div className="settings-header">
                        <div className="settings-icon emerald">
                            <DollarSign size={24} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">Currency Matrix</h3>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Base Rate Comparisons (USD)</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {currencies.map((curr, idx) => (
                            <div key={curr.code} className="currency-row">
                                <div className="currency-code text-sm">
                                    {curr.code}
                                </div>
                                <div className="flex-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 block ml-1">Exchange Rate</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                                            value={curr.rate}
                                            onChange={(e) => {
                                                const newCurrs = [...currencies];
                                                newCurrs[idx].rate = e.target.value;
                                                setCurrencies(newCurrs);
                                            }}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-black text-slate-300 uppercase tracking-widest">Per 1 USD</span>
                                    </div>
                                </div>
                                <div className="text-xl font-black text-slate-300">
                                    {curr.symbol}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button className="mt-8 text-indigo-600 font-bold text-xs uppercase tracking-widest flex items-center gap-2 hover:gap-3 transition-all">
                        <RefreshCw size={14} /> Fetch Live Rates
                    </button>
                </div>

                {/* Operational Thresholds */}
                <div className="settings-card dark shadow-2xl">
                    <div className="settings-header">
                        <div className="settings-icon indigo">
                            <Activity size={24} strokeWidth={2.5} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-white uppercase tracking-tight">Lesson Guardrails</h3>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Policy Enforcement</p>
                        </div>
                    </div>

                    <div className="space-y-10">
                        <div className="threshold-group">
                            <div className="threshold-label-wrap">
                                <label className="threshold-label">Min Units for Test</label>
                                <span className="threshold-value">{thresholds.minLessonsForTest}</span>
                            </div>
                            <input
                                type="range"
                                min="5"
                                max="50"
                                className="range-slider"
                                value={thresholds.minLessonsForTest}
                                onChange={e => setThresholds({ ...thresholds, minLessonsForTest: parseInt(e.target.value) })}
                            />
                        </div>

                        <div className="threshold-group">
                            <div className="threshold-label-wrap">
                                <label className="threshold-label">Max Load Per Day</label>
                                <span className="threshold-value" style={{ color: '#a855f7' }}>{thresholds.maxLessonsPerDay}</span>
                            </div>
                            <input
                                type="range"
                                min="1"
                                max="8"
                                className="range-slider"
                                style={{ accentColor: '#a855f7' }}
                                value={thresholds.maxLessonsPerDay}
                                onChange={e => setThresholds({ ...thresholds, maxLessonsPerDay: parseInt(e.target.value) })}
                            />
                        </div>

                        <div className="threshold-group">
                            <div className="threshold-label-wrap">
                                <label className="threshold-label">Cancellation Lock</label>
                                <span className="threshold-value" style={{ color: '#f43f5e' }}>{thresholds.cancellationWindowHours}h</span>
                            </div>
                            <input
                                type="range"
                                min="6"
                                max="72"
                                className="range-slider"
                                style={{ accentColor: '#f43f5e' }}
                                value={thresholds.cancellationWindowHours}
                                onChange={e => setThresholds({ ...thresholds, cancellationWindowHours: parseInt(e.target.value) })}
                            />
                        </div>
                    </div>

                    <div className="mt-auto pt-10 grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3">
                            <Shield size={20} className="text-indigo-400" />
                            <div className="text-[9px] font-bold text-slate-400 leading-tight uppercase tracking-widest">Strict Mode Enabled</div>
                        </div>
                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3">
                            <Bell size={20} className="text-amber-400" />
                            <div className="text-[9px] font-bold text-slate-400 leading-tight uppercase tracking-widest">Notify on Violations</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
